import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "./Topbar.css";
import { getKolkataHour, getKolkataMonthDay } from "../utils/dateTime";
import { ADMIN_SESSION_EVENT, getSelectedAdminSession } from "../utils/adminSession";
import { sweetAlert } from "../utils/sweetAlerts";
import NotificationBell from "./NotificationBell";
import { RUNTIME_PUBLIC_BASE, RUNTIME_API_BASE } from "../utils/runtimeBase";

const API = RUNTIME_API_BASE;

function Topbar({ user, logout, setMobileMenu, screenSize }) {
  const [showProfile, setShowProfile] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "", new_password_confirmation: "" });
  const [passwordMessage, setPasswordMessage] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);
  const [currentUser, setCurrentUser] = useState(user || {});
  const [savingProfileImage, setSavingProfileImage] = useState(false);
  const profileRef = useRef();
  const imageInputRef = useRef(null);

  const getWish = () => {
    const hour = getKolkataHour();
    if (hour < 12) return "Good Morning";
    if (hour < 17) return "Good Afternoon";
    if (hour < 21) return "Good Evening";
    return "Good Night";
  };

  const getBirthdayWish = () => {
    const dob = "04-16";
    const todayStr = getKolkataMonthDay();
    return todayStr === dob ? "Happy Birthday Sir!" : "";
  };

  useEffect(() => {
    setCurrentUser(user || {});
  }, [user]);

  const resolveProfileImage = (image) => {
    if (!image) return `https://ui-avatars.com/api/?name=${encodeURIComponent(currentUser?.name || "Admin")}`;
    if (String(image).startsWith("http") || String(image).startsWith("data:")) return image;
    return `${RUNTIME_PUBLIC_BASE}/uploads/${image}`;
  };

  const profileImage = resolveProfileImage(currentUser?.image);
  const titleText = "National Institute of Digital Careers and Skills";
  const subtitleText = `${getWish()} ${currentUser?.name || currentUser?.login_id || "Admin"}`;

  useEffect(() => {
    let active = true;

    const loadTopbarData = async () => {
      try {
        const statsRes = await axios.get(`${API}/dashboard`, {
          params: {
            session: getSelectedAdminSession(),
            _ts: Date.now(),
          },
        });

        if (!active) return;
        localStorage.setItem("adminDashboardStats", JSON.stringify(statsRes.data || {}));
      } catch (error) {
        if (!active) return;
      }
    };

    loadTopbarData();
    const timer = window.setInterval(loadTopbarData, 20000);
    const handleDashboardStats = () => loadTopbarData();

    window.addEventListener("admin-dashboard-stats-updated", handleDashboardStats);
    window.addEventListener("academic-session-changed", handleDashboardStats);
    window.addEventListener(ADMIN_SESSION_EVENT, handleDashboardStats);

    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener("admin-dashboard-stats-updated", handleDashboardStats);
      window.removeEventListener("academic-session-changed", handleDashboardStats);
      window.removeEventListener(ADMIN_SESSION_EVENT, handleDashboardStats);
    };
  }, []);

  useEffect(() => {
    const handleClick = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfile(false);
      }
    };

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const changePassword = async (event) => {
    event.preventDefault();
    try {
      setSavingPassword(true);
      setPasswordMessage("");
      const res = await axios.post(`${API}/admin/change-password`, {
        user_id: currentUser?.id,
        role: currentUser?.role || "admin_user",
        ...passwordForm,
      });
      setPasswordMessage(res.data?.message || "Password changed successfully");
      setPasswordForm({ current_password: "", new_password: "", new_password_confirmation: "" });
    } catch (error) {
      setPasswordMessage(error.response?.data?.message || "Password change failed");
    } finally {
      setSavingPassword(false);
    }
  };

  const updateProfileImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setSavingProfileImage(true);
      const formData = new FormData();
      formData.append("user_id", currentUser?.id ?? 0);
      formData.append("role", currentUser?.role || "admin_user");
      formData.append("image", file);

      const res = await axios.post(`${API}/admin/profile-image`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const nextUser = { ...currentUser, image: res.data?.image };
      setCurrentUser(nextUser);
      localStorage.setItem("user", JSON.stringify(nextUser));
      await sweetAlert(res.data?.message || "Profile image updated successfully", { icon: "success" });
    } catch (error) {
      await sweetAlert(error.response?.data?.message || "Profile image update failed", { icon: "error" });
    } finally {
      setSavingProfileImage(false);
      if (imageInputRef.current) {
        imageInputRef.current.value = "";
      }
    }
  };

  return (
    <div
      style={{
        background: "linear-gradient(120deg, rgba(219,234,254,0.96) 0%, rgba(248,250,252,0.97) 44%, rgba(255,237,213,0.98) 100%)",
        padding: screenSize <= 768 ? "12px 14px" : "14px 20px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "14px",
        position: "sticky",
        top: 0,
        zIndex: 1500,
        borderBottom: "1px solid rgba(148, 163, 184, 0.18)",
        boxShadow: "0 18px 40px rgba(148, 163, 184, 0.12)",
        flexWrap: "wrap",
        backdropFilter: "blur(12px)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", minWidth: 0 }}>
        {screenSize <= 768 && (
          <button
            type="button"
            onClick={() => setMobileMenu(true)}
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "999px",
              background: "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)",
              border: "none",
              fontSize: "20px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#ffffff",
              padding: 0,
              boxShadow: "0 14px 24px rgba(124, 45, 58, 0.26)",
              flexShrink: 0,
            }}
          >
            <i className="bi bi-list"></i>
          </button>
        )}

        <div style={{ minWidth: 0 }}>
          <h4
            style={{
              margin: "0 0 6px",
              fontSize: screenSize <= 768 ? "18px" : "23px",
              color: "#4e1b24",
              fontWeight: 800,
              lineHeight: 1.2,
            }}
          >
            {titleText}
          </h4>

          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "5px 11px",
              borderRadius: "999px",
              background: "rgba(255,255,255,0.72)",
              border: "1px solid rgba(148, 163, 184, 0.22)",
              fontSize: "12px",
              color: "#7c2d3a",
              maxWidth: "100%",
            }}
          >
            <i className="bi bi-stars"></i>
            <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{subtitleText}</span>
          </div>

          {getBirthdayWish() && (
            <div style={{ overflow: "hidden", whiteSpace: "nowrap", width: "100%", marginTop: "6px" }}>
              <div
                style={{
                  display: "inline-block",
                  color: "#7c2d3a",
                  fontSize: "13px",
                  fontWeight: 600,
                  paddingLeft: "100%",
                  animation: "scrollText 10s linear infinite",
                }}
              >
                {getBirthdayWish()}
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
        <NotificationBell actorType="admin" actorId={currentUser?.id ?? 0} actorName={currentUser?.name || currentUser?.login_id || "Admin"} />

        <div ref={profileRef} style={{ position: "relative" }}>
          <button
            type="button"
            onClick={() => setShowProfile(!showProfile)}
            style={{
              width: "42px",
              height: "42px",
              borderRadius: "999px",
              border: "1px solid rgba(148, 163, 184, 0.18)",
              background: "rgba(255,255,255,0.72)",
              cursor: "pointer",
              padding: 0,
              overflow: "hidden",
            }}
          >
            <img
              src={profileImage}
              alt="Admin profile"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </button>

          {showProfile && (
            <div
              style={{
                position: "absolute",
                right: 0,
                top: "58px",
                width: "260px",
                background: "#fff",
                borderRadius: "22px",
                boxShadow: "0 22px 50px rgba(15, 23, 42, 0.18)",
                border: "1px solid rgba(148, 163, 184, 0.16)",
                padding: "18px",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <img
                  src={profileImage}
                  alt="Admin profile"
                  style={{ width: "72px", height: "72px", borderRadius: "50%", objectFit: "cover" }}
                />
                <h6 style={{ marginTop: "12px", marginBottom: "4px", color: "#0f172a", fontSize: "18px" }}>{currentUser?.name || currentUser?.login_id || "Admin"}</h6>
                <small style={{ color: "#64748b" }}>{currentUser?.email || "master admin access"}</small>
              </div>

              <hr />

              <input ref={imageInputRef} type="file" accept="image/*" onChange={updateProfileImage} style={{ display: "none" }} />
              <button
                type="button"
                className="btn btn-outline-primary w-100 mb-2"
                style={{ borderRadius: "12px" }}
                onClick={() => imageInputRef.current?.click()}
                disabled={savingProfileImage}
              >
                {savingProfileImage ? "Uploading..." : "Update Profile Image"}
              </button>

              <button
                className="btn btn-outline-dark w-100 mb-2"
                style={{ borderRadius: "12px" }}
                onClick={() => setShowPasswordForm((prev) => !prev)}
              >
                Change Password
              </button>

              {showPasswordForm ? (
                <form onSubmit={changePassword} style={{ display: "grid", gap: "10px", marginBottom: "12px", textAlign: "left" }}>
                  <input
                    type="password"
                    className="form-control"
                    placeholder="Current password"
                    value={passwordForm.current_password}
                    onChange={(event) => setPasswordForm((prev) => ({ ...prev, current_password: event.target.value }))}
                    required
                  />
                  <input
                    type="password"
                    className="form-control"
                    placeholder="New password"
                    value={passwordForm.new_password}
                    onChange={(event) => setPasswordForm((prev) => ({ ...prev, new_password: event.target.value }))}
                    required
                    minLength={4}
                  />
                  <input
                    type="password"
                    className="form-control"
                    placeholder="Confirm new password"
                    value={passwordForm.new_password_confirmation}
                    onChange={(event) => setPasswordForm((prev) => ({ ...prev, new_password_confirmation: event.target.value }))}
                    required
                    minLength={4}
                  />
                  <button type="submit" className="btn btn-primary w-100" style={{ borderRadius: "12px" }} disabled={savingPassword}>
                    {savingPassword ? "Saving..." : "Save Password"}
                  </button>
                  {passwordMessage ? (
                    <small style={{ color: passwordMessage.toLowerCase().includes("success") ? "#166534" : "#b91c1c", fontWeight: 700 }}>
                      {passwordMessage}
                    </small>
                  ) : null}
                </form>
              ) : null}

              <button className="btn btn-outline-danger w-100" style={{ borderRadius: "12px" }} onClick={logout}>
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Topbar;



